import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { ReactComponent as ChevronUp } from "@assets/icons/inbox/chevron-up.svg";
import { ReactComponent as ChevronDown } from "@assets/icons/inbox/chevron-down.svg";

import { Checkbox } from "@/components/Checkbox/Checkbox";
import { classNames } from "@/utils/classNames";

import { CampaignListGroup } from "./types";
import { CampaignRow } from "./CampaignRow";

interface ListGroupRowProps {
	group: CampaignListGroup;
	isExpanded: boolean;
	isFullySelected: boolean;
	isPartiallySelected: boolean;
	selectedIds: Set<string>;
	onToggleList: (group: CampaignListGroup) => void;
	onToggleExpand: (listId: number) => void;
	onToggleCampaign: (campaignId: string) => void;
}

export const ListGroupRow: FunctionComponent<ListGroupRowProps> = ({
	group,
	isExpanded,
	isFullySelected,
	isPartiallySelected,
	selectedIds,
	onToggleList,
	onToggleExpand,
	onToggleCampaign,
}) => {
	const { t } = useTranslation("translation", { keyPrefix: "list" });

	const Chevron = isExpanded ? ChevronUp : ChevronDown;

	return (
		<div className="border-b border-b-border last:border-b-0">
			<div
				className={classNames(
					"flex items-center gap-2 h-11 px-3 cursor-pointer duration-200",
					isFullySelected || isPartiallySelected ? "bg-selected-item" : "hover:bg-gray-100"
				)}
				data-test="copy-campaign-list-row"
				onClick={() => onToggleList(group)}
			>
				<div className="w-5 flex-none">
					<Checkbox isChecked={isFullySelected} isIndeterminate={isPartiallySelected} />
				</div>
				<span className="grow text-sm font-medium text-gray-900 truncate">{group.listName}</span>
				<span className="flex-shrink-0 text-xs text-text-meta">
					{t("copyCampaigns.campaignCount", { count: group.campaigns.length })}
				</span>
				<button
					aria-label="toggle"
					className="flex-shrink-0 p-1 rounded-md text-gray-500 hover:bg-gray-200 duration-200"
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						onToggleExpand(group.listId);
					}}
				>
					<Chevron className="w-4 h-4" />
				</button>
			</div>

			{isExpanded && (
				<div className="bg-gray-50">
					{group.campaigns.map((campaign) => (
						<CampaignRow
							key={campaign.id}
							campaign={campaign}
							isSelected={selectedIds.has(campaign.id)}
							onToggle={onToggleCampaign}
						/>
					))}
				</div>
			)}
		</div>
	);
};
